import Vue from 'vue'
import { cloneDeep, isEqual, pickBy } from 'lodash'
import { helpers } from '@/plugins'

// hydrate() initialise à 0 toutes les options du plan, y compris celles que le client
// ne détient pas : une option à 0 ne constitue donc pas une modification de sa part.
const usedOptions = options => pickBy(options, quantity => quantity > 0)

const LEGACY_OPTION = 'max_monitor_plus_100'
const CURRENT_OPTION = 'max_monitor_plus_100_2026'

export default ({ api, helpers, moment, module }) => {
  return {
    get () {
      return new Vue({
        data () {
          return {
            status: 'order',
            activeSubscription: null,
            module: null,
            plan: null,
            yearly: true,
            pricing: {
              enabled: false,
              yearly: false,
              monthly: false
            },
            options: {},
            period: {
              start: null,
              end: null
            },
            ends_at: null,
            update_at_end: null,
            coupon: {
              id: null,
              code: null,
              percentage: null,
              amount: null
            },
            tax: {
              enabled: true,
              amount: 20
            },
            hydrated: false
          }
        },
        watch: {
          yearly (yearly) {
            if (this.status === 'order') {
              this.computeOrderPeriod(yearly)
            }
          }
        },
        computed: {
          // Les clients d'avant 2026 sont restés sur l'option historique. Tout ce qui
          // affiche, tarifie ou modifie leurs mots-clés doit suivre l'option qu'ils
          // détiennent, pas celle du catalogue courant — y compris les arguments de plan
          // évalués depuis les fichiers de traduction.
          optionKey () {
            const active = this.activeSubscription
            return active && active.options && active.options[LEGACY_OPTION] > 0
              ? LEGACY_OPTION
              : CURRENT_OPTION
          },
          moduleConfig () {
            return module.all[this.module]
          },
          modulePlanConfig () {
            return module.all[this.module].plan.list[this.plan]
          },
          durationPriceNoTax () {
            return this.yearly ? this.yearlyPriceNoTax : this.monthlyPriceNoTax
          },
          durationPriceWithTax () {
            return this.yearly ? this.yearlyPriceWithTax : this.monthlyPriceWithTax
          },
          monthlyPlanBasePrice () {
            if (!this.plan) return null

            if (this.yearly) return this.modulePlanConfig.stripe.prices.year.amount / 12
            else return this.modulePlanConfig.stripe.prices.month.amount
          },
          monthlyPriceNoTax () {
            if (!this.plan) return null

            let price = this.monthlyPlanBasePrice

            // Add options prices to base plan price
            for (const [optionName, optionCount] of Object.entries(this.options)) {
              if (this.yearly) price += (this.moduleConfig.plan.options[optionName].stripe.prices.year.amount / 12) * optionCount
              else price += this.moduleConfig.plan.options[optionName].stripe.prices.month.amount * optionCount
            }

            // Remove the coupon amount if specified
            if (this.coupon.id !== null) {
              price -= this.coupon.percentage ? price * (this.coupon.amount / 100) : this.coupon.amount
            }

            return price
          },
          monthlyPriceWithTax () {
            let price = this.monthlyPriceNoTax

            // Add tax if specified
            if (this.tax.enabled) {
              price += price * (this.tax.amount / 100)
            }

            return price
          },
          yearlyPriceNoTax () {
            return this.monthlyPriceNoTax * 12
          },
          yearlyPriceWithTax () {
            return this.monthlyPriceWithTax * 12
          },
        },
        methods: {
          applyCouponCode (code) {
            this.coupon.code = code
            if (code === '') {
              this.coupon.id = null
              this.coupon.percentage = null
              this.coupon.amount = null
              return Promise.resolve()
            } else {
              return new Promise(((resolve, reject) => {
                api.post('account/customer/coupon/' + code, { estimate: this.toArray() }, { notifyErrors: false })
                  .then(response => {
                    this.coupon.id = response.data.id
                    this.coupon.percentage = response.data.percentage
                    this.coupon.amount = response.data.amount

                    if (this.coupon.percentage) resolve('Réduction de ' + this.coupon.amount + '% appliquée sur votre commande.')
                    else resolve('Réduction de ' + helpers.present.currency(this.coupon.amount) + ' appliquée sur votre commande.')
                  })
                  .catch(() => reject('Erreur, votre code de réduction n\'est pas valide'))
              }))
            }
          },
          computeOrderPeriod (yearly) {
            this.period.start = moment()
            this.period.end = yearly ? moment().clone().add(1, 'year') : moment().clone().add(1, 'month')
          },
          hydrate (module, plan, activeSubscription) {
            this.module = module
            this.plan = plan

            // Load available pricing from config
            if ('stripe' in this.modulePlanConfig && 'prices' in this.modulePlanConfig.stripe) {
              this.pricing.enabled = true
              this.pricing.yearly = 'year' in this.modulePlanConfig.stripe.prices
              this.pricing.monthly = 'month' in this.modulePlanConfig.stripe.prices
              this.yearly = !!this.pricing.yearly
            }

            // Compute the period
            this.computeOrderPeriod(this.yearly)

            // Load available options from config
            if (this.modulePlanConfig.options) {
              this.modulePlanConfig.options.forEach(option => {
                this.$set(this.options, option, 0)
              })
            }

            // Load values from active subscription
            if (activeSubscription) {
              // Save the subscription (allows us to detect modifications) and re-create array for avoiding side effets
              this.activeSubscription = cloneDeep(activeSubscription)

              this.status = activeSubscription.status
              this.yearly = activeSubscription.yearly
              for (const [name, quantity] of Object.entries(activeSubscription.options)) {
                this.$set(this.options, name, quantity)
              }

              this.period.start = activeSubscription.period.start
              this.period.end = activeSubscription.period.end
              this.ends_at = activeSubscription.ends_at
              this.update_at_end = activeSubscription.update_at_end

              if (activeSubscription.coupon) {
                this.coupon.id = activeSubscription.coupon.id
                this.coupon.percentage = activeSubscription.coupon.percentage
                this.coupon.amount = activeSubscription.coupon.amount
              } else {
                this.coupon.id = null
              }

              if (activeSubscription.tax) {
                this.tax.enabled = true
                this.tax.amount = activeSubscription.tax.amount
              } else {
                this.tax.enabled = false
              }
            }

            this.hydrated = true
          },
          hasModifications () {
            if (this.activeSubscription) {
              if (this.yearly !== this.activeSubscription.yearly) return true
              if (!isEqual(usedOptions(this.options), usedOptions(this.activeSubscription.options))) return true
            }

            return false
          },
          toArray () {
            return {
              module: this.module,
              plan: this.plan,
              options: this.options,
              yearly: this.yearly,
              coupon: this.coupon,
              tax: this.tax
            }
          },
          create (useDefaultPaymentMethod) {
            if (this.status !== 'order') return Promise.reject()
            else {
              return new Promise((resolve, reject) => {
                api.post('account/customer/subscription', {
                  ...this.toArray(),
                  use_default_payment_method: !!useDefaultPaymentMethod
                }, { showLoading: false })
                  .then(response => resolve(response.data))
                  .catch(err => reject(err.message))
              })
            }
          },
          update (immediateDowngrade) {
            if (!['active', 'trialing'].includes(this.status)) return Promise.reject()
            else {
              return new Promise((resolve, reject) => {
                return api.put('account/customer/subscription', {
                  ...this.toArray(),
                  immediateDowngrade
                }, { showLoading: false })
                  .then(response => resolve(response.data))
                  .catch(err => reject(err.message))
              })
            }
          },
          terminate (immediate) {
            return new Promise((resolve, reject) => {
              api.delete('account/customer/subscription', {
                module: this.module,
                immediate: immediate
              }, { showLoading: false })
                .then(() => resolve())
                .catch(err => reject(err.message))
            })
          },
          resume () {
            return new Promise((resolve, reject) => {
              api.post('account/customer/subscription/resume', {
                module: this.module
              }, { showLoading: false })
                .then(() => resolve())
                .catch(err => reject(err.message))
            })
          }
        }
      })
    }
  }
}